// views/Dashboard/Bonuses/Bonuses.tsx
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import BonusCard from './components/BonusCard/BonusCard';
import BonusDetailModal from './components/BonusDetailModal/BonusDetailModal';
import CustomBonusForm from './components/CustomBonusForm/CustomBonusForm';
import Loading from '../../../components/common/Loading/Loading';
import Button from '../../../components/common/Button/Button';
import Modal from '../../../components/common/Modal/Modal';
import { Bonus, BonusProgress, BonusType, Risk, SpecialMark } from '../../../Entities';
import { notify } from '../../../components/common/Toast/toastBridge';
import styles from './Bonuses.module.css';

type SortBy = 'risk' | 'profit' | 'endDate';
type SortOrder = 'asc' | 'desc';

const getRiskId = (riskName: any): number => Risk.values.filter((o) => o.rate === riskName)[0].index as number;

const Bonuses = () => {
  const { user, setUser } = useAuth();
  const [selectedBonus, setSelectedBonus] = useState<any>(null);
  const [showCustomBonusForm, setShowCustomBonusForm] = useState(false);
  const [activeTypeFilters, setActiveTypeFilters] = useState<string[]>([]);
  const [activeRiskFilters, setActiveRiskFilters] = useState<any[]>([]);
  const [activeMarkFilters, setActiveMarkFilters] = useState<string[]>([]);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [availableMarks] = useState<string[]>(['VIP', 'recommended', 'exceptional']);
  const [sortBy, setSortBy] = useState<SortBy>('endDate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [allBonuses, setAllBonuses] = useState<any[] | null>(null);
  const navigate = useNavigate();

  const prepareBonuses = useCallback(async () => {
    try {
      const bonuses = await Bonus.getAll();
      const allTypes = [...new Set(bonuses.map((b) => b.bonus_type_name))] as string[];
      setAllBonuses(bonuses);
      setAvailableTypes(allTypes);
    } catch (error) {
      console.error(error);
      notify.error('Nepodařilo se načíst bonusy');
      navigate('/dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    prepareBonuses();
  }, [prepareBonuses]);

  const sortBonuses = useCallback(
    (bonuses: any[]) =>
      [...bonuses].sort((a, b) => {
        let aValue: number;
        let bValue: number;
        switch (sortBy) {
          case 'risk':
            aValue = getRiskId(a.risk_rate);
            bValue = getRiskId(b.risk_rate);
            break;
          case 'profit':
            aValue = a.expected_value;
            bValue = b.expected_value;
            break;
          case 'endDate':
            aValue = new Date(a.end_timestamp * 1000).getTime();
            bValue = new Date(b.end_timestamp * 1000).getTime();
            break;
          default:
            return 0;
        }
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }),
    [sortBy, sortOrder],
  );

  const filteredBonuses = useMemo(() => {
    if (!allBonuses) return [];
    const filtered = allBonuses.filter((bonus) => {
      if (activeTypeFilters.length && !activeTypeFilters.includes(bonus.bonus_type_name)) return false;
      if (activeRiskFilters.length && !activeRiskFilters.includes(bonus.risk_rate)) return false;
      if (activeMarkFilters.length && !activeMarkFilters.includes(bonus.special_mark)) return false;
      return true;
    });
    return sortBonuses(filtered);
  }, [allBonuses, activeTypeFilters, activeRiskFilters, activeMarkFilters, sortBonuses]);

  const hideBonus = async (bonus: any) => {
    try {
      await bonus.hide();
      setAllBonuses((prev) => (prev ? prev.filter((b) => b.id !== bonus.id) : prev));
    } catch (error) {
      console.error(error);
      throw error; // rethrow so BonusCard can surface the failure
    }
  };

  const updateStepIdx = async (bonusId: number, newStepIdx: number) => {
    try {
      await BonusProgress.updateStepIdx(bonusId, newStepIdx);
      const updatedBonus = new Bonus({ ...selectedBonus, step_idx: newStepIdx });
      setSelectedBonus(updatedBonus);
      setAllBonuses((prev) => (prev ? prev.map((b) => (b.id === bonusId ? updatedBonus : b)) : prev));
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleSortChange = (newSortBy: SortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  const getSortLabel = (sortType: SortBy) =>
    ({ risk: 'Riziko', profit: 'Zisk', endDate: 'Konec platnosti' }[sortType] || '');
  const getSortIcon = (sortType: SortBy) => (sortBy !== sortType ? '↕️' : sortOrder === 'asc' ? '⬆️' : '⬇️');

  const toggle = <T,>(setter: React.Dispatch<React.SetStateAction<T[]>>) => (value: T) =>
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const toggleTypeFilter = toggle(setActiveTypeFilters);
  const toggleMarkFilter = toggle(setActiveMarkFilters);
  const toggleRiskFilter = toggle(setActiveRiskFilters);

  if (!allBonuses) return <Loading />;

  const isNotPremium = user?.tier?.name?.toLowerCase() !== 'premium';

  return (
    <div className={styles.bonusesPage}>
      <div className={styles.header}>
        <h1>
          Dostupné bonusy -{' '}
          <span className={styles.sumPotential} data-tour="bonuses-sum">
            {filteredBonuses.reduce((acc, b) => acc + Number(b.expected_value), 0)} Kč je Váš možný zisk
          </span>
        </h1>
      </div>

      <div className={styles.content}>
        <div className={styles.filtersContainer} data-tour="bonus-filters">
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Typ bonusu:</span>
            <div className={styles.typeFilters}>
              {availableTypes.map((type) => {
                const typeInfo = BonusType.get({ name: type });
                const isActive = activeTypeFilters.includes(type);
                return (
                  <button
                    key={type}
                    className={`${styles.typeFilter} ${isActive ? styles.active : styles.inactive}`}
                    onClick={() => toggleTypeFilter(type)}
                    style={{
                      borderColor: isActive ? typeInfo.color : 'var(--line)',
                      backgroundColor: isActive ? `${typeInfo.color}20` : 'transparent',
                    }}
                  >
                    <span className={styles.typeIcon}>{typeInfo.icon}</span>
                    <span>{typeInfo.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Označení:</span>
            <div className={styles.markFilters}>
              {availableMarks.map((mark) => {
                const isActive = activeMarkFilters.includes(mark);
                const markInfo = SpecialMark.get({ name: mark });
                return (
                  <button
                    key={mark}
                    style={{
                      borderColor: isActive ? markInfo.color : 'var(--line)',
                      backgroundColor: isActive ? markInfo.color : 'transparent',
                    }}
                    className={`${styles.markFilter} ${isActive ? styles.active : styles.inactive}`}
                    onClick={() => toggleMarkFilter(mark)}
                  >
                    <span>{markInfo.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Rizikovost:</span>
            <div className={styles.riskFilters}>
              {Risk.values.map((risk) => {
                const isActive = activeRiskFilters.includes(risk.rate);
                return (
                  <button
                    key={risk.rate}
                    className={`${styles.riskFilter} ${isActive ? styles.active : styles.inactive}`}
                    onClick={() => toggleRiskFilter(risk.rate)}
                    title={risk.label}
                  >
                    <div
                      className={styles.riskDot}
                      style={{
                        backgroundColor: isActive ? risk.color : risk.color_not_clicked,
                        boxShadow: isActive ? `0 0 10px ${risk.color}40` : 'none',
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.filterGroup} data-tour="bonus-sort">
            <span className={styles.filterLabel}>Řadit podle:</span>
            <div className={styles.sortFilters}>
              {(['risk', 'profit', 'endDate'] as SortBy[]).map((sortType) => (
                <button
                  key={sortType}
                  className={`${styles.sortFilter} ${sortBy === sortType ? styles.active : styles.inactive}`}
                  onClick={() => handleSortChange(sortType)}
                >
                  <span>{getSortLabel(sortType)}</span>
                  <span className={styles.sortIcon}>{getSortIcon(sortType)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.addCustomBonusDiv} data-tour="custom-bonus-btn">
          <Button
            variant="primary"
            onClick={() => setShowCustomBonusForm(true)}
            title={isNotPremium ? 'upgradujte tier' : 'evidovat bonus'}
            disabled={isNotPremium}
            className={styles.customBonusButton}
          >
            ➕ Evidovat custom bonus
            {isNotPremium && <span className={styles.lockBadge}>Premium+</span>}
          </Button>
        </div>

        <div className={styles.bonusesGrid} data-tour="bonuses-grid">
          {filteredBonuses.map((bonus) => (
            <BonusCard
              key={bonus.id}
              bonus={bonus}
              hideBonus={hideBonus}
              onDetail={() => setSelectedBonus(bonus)}
              locked={false}
            />
          ))}
        </div>

        {filteredBonuses.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>🔍</div>
            <h3>Žádné bonusy nenalezeny</h3>
            <p>Zkuste upravit filtry nebo upgradujte členství pro více bonusů.</p>
          </div>
        )}

        {selectedBonus && (
          <BonusDetailModal
            bonus={selectedBonus}
            onClose={() => setSelectedBonus(null)}
            onBonusFilled={() => {
              setAllBonuses((prev) => (prev ? prev.filter((b) => b.id !== selectedBonus.id) : prev));
              setSelectedBonus(null);
            }}
            updateStepIdx={updateStepIdx}
            user={user}
            setUser={setUser}
          />
        )}

        {showCustomBonusForm && (
          <Modal isOpen={showCustomBonusForm} onClose={() => setShowCustomBonusForm(false)} title="Evidence custom bonusu" size="large">
            <CustomBonusForm onClose={() => setShowCustomBonusForm(false)} />
          </Modal>
        )}
      </div>
    </div>
  );
};

export default Bonuses;
